import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay } from "@chakra-ui/react";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import { Select } from "../../../components/Select";
import { postWithHeaders } from "../../../api/api";
import useLocalStorage from "../../../hooks/useLocalStorage";
import { typedSxMap } from "../../../utils/typedSxMap";

const styles = typedSxMap({
  overlay: {
    opacity: 0.8,
  },
  header: {
    display: "flex",
    alignItems: "center",
    margin: 0,
    padding: 0,
  },
  modalWrapper: {
    borderRadius: "var(--border-radius-5)",
    padding: "var(--m)",
    backgroundColor: "var(--color-background-modal)",
    display: "flex",
    gap: "24px",
  },
  container: {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    "& input": {
      fontSize: "16px",
    },
  },
  formLabel: {},
  footer: {
    display: "flex",
    padding: 0,
    justifyContent: "space-between",
  },
});

type UploadFileModalProps = {
  isOpen: boolean;
  workspaceId: string;
  selectedFile: File | null;
  schemaOptions: { [key: string]: { value: string; key: string; name: string } };
  onClose: () => void;
};

export default function UploadFileModal({ isOpen, workspaceId, onClose, selectedFile, schemaOptions }: UploadFileModalProps) {
  const [fileName, setFileName] = useState<string>("");
  const [selectedSchemaId, setSelectedSchemaId] = useLocalStorage("selectedSchemaId", "");
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const schemas = Object.values(schemaOptions);
    if (schemas?.length && isOpen) {
      const selectedSchema = schemas.find((schema) => schema.value === selectedSchemaId);
      setSelectedSchemaId(selectedSchema?.value || schemas[0].value);
    }
  }, [schemaOptions, isOpen]);

  useEffect(() => {
    if (selectedFile && isOpen) {
      setFileName(selectedFile.name);
    }
  }, [selectedFile, isOpen]);

  const handleUpdateFileName = async (event: any) => {
    setFileName(event.target.value);
  };

  const handleFileUpload = async (event: any) => {
    setIsLoading(true);
    if (!selectedFile) {
      return;
    }
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("schema_id", selectedSchemaId || "");
    formData.append("name", fileName);

    try {
      await postWithHeaders(`extractions/${workspaceId}/upload`, formData, {}, true);
      await queryClient.invalidateQueries(["pipeline-imports", workspaceId]);
      onClose();
      setFileName("");
      setSelectedSchemaId(null);
    } catch (error) {
      console.error("Error uploading file: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      isCentered={true}>
      <ModalOverlay sx={styles.overlay} />
      <ModalContent sx={styles.modalWrapper}>
        <ModalHeader sx={styles.header}>
          <h2> Upload File</h2>
        </ModalHeader>
        <ModalBody sx={styles.container}>
          <Select
            options={schemaOptions}
            label="Schema"
            name="schema"
            value={selectedSchemaId || ""}
            onChange={(selectedSchema: any) => {
              selectedSchema && setSelectedSchemaId((selectedSchema as any) || "");
            }}
            disabled={isLoading}
          />
          <Input
            type="text"
            onChange={handleUpdateFileName}
            defaultValue={selectedFile?.name}
            label="File Name"
            autoComplete="off"
            disabled={isLoading}
          />
        </ModalBody>
        <ModalFooter sx={styles.footer}>
          <Button type="button" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variants={["primary", "noMargin"]}
            disabled={isLoading || !selectedFile || !selectedSchemaId}
            onClick={handleFileUpload}>
            Upload
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
